import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, addDoc } from "firebase/firestore";
import { getCachePropiedades, setCachePropiedades, cacheEsFresco } from '../utils/cachePropiedades';
import { useConfigSitio } from '../hooks/useConfigSitio';
import useFavoritos from '../hooks/useFavoritos';
import FranjaRemates from '../components/FranjaRemates';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Tipos canónicos del combo de filtro. "Otro" matchea cualquier tipo fuera de esta lista.
const TIPOS_CONOCIDOS = ['Casa', 'Casa Quinta', 'Campo', 'Departamento', 'Complejo de departamentos', 'Terreno', 'Local', 'Oficina', 'Galpón'];

// Precio formateado; si no hay precio (vacío/0) muestra "Consultar precio"
const precioTexto = p => p.precio ? `${p.moneda === 'USD' ? 'U$S' : '$'} ${Number(p.precio).toLocaleString('es-AR')}` : 'Consultar precio';

// Ambas características usan solo el campo dedicado; lo completa cada corredor si corresponde.
const esAptoCredito = p => p.aptoCredito === true;
const esAptoCreditoSinPlano = p => p.aptoCreditoSinPlano === true;

async function copiarAlPortapapeles(texto) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(texto);
  } else {
    const el = document.createElement('textarea');
    el.value = texto; el.style.position = 'fixed'; el.style.opacity = '0';
    document.body.appendChild(el); el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
}
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

function estaVigente(p) {
  if (p.publicada === false) return false;
  if (!p.fechaCaducidad) return !!p.renovacionAutomatica;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const caduca = new Date(p.fechaCaducidad);
  if (isNaN(caduca)) return !!p.renovacionAutomatica;
  if (caduca >= hoy) return true;
  return !!p.renovacionAutomatica;
}


export default function Home() {
  const config = useConfigSitio();
  const [propiedades, setPropiedades] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const sp = (k, def = '') => searchParams.get(k) || def;
  const { toggle, esFavorito, cantidad: cantFav } = useFavoritos();
  const [soloFavoritos, setSoloFavoritos] = useState(false);

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState("grilla");
  const [orden, setOrden] = useState(() => sp('orden', 'mezcla'));
  // Semilla para el orden "mezclado": estable durante la sesión (no se reordena al
  // volver de una ficha) y distinta en cada visita nueva (rota qué martillero sale primero).
  const [seedOrden] = useState(() => {
    let s = sessionStorage.getItem('ordenSeed');
    if (!s) { s = String(Math.floor(Math.random() * 1e9)); sessionStorage.setItem('ordenSeed', s); }
    return parseInt(s);
  });
  const hashOrden = (id) => {
    let h = seedOrden >>> 0;
    const str = String(id || '');
    for (let i = 0; i < str.length; i++) h = (Math.imul(h, 31) + str.charCodeAt(i)) >>> 0;
    return h;
  };
  const [pagina, setPagina] = useState(() => parseInt(sp('pagina')) || 1);
  const [porPagina, setPorPagina] = useState(() => {
    const v = parseInt(localStorage.getItem('porPaginaCatalogo'));
    return [12, 24, 48].includes(v) ? v : 24;
  });
  const [masFiltros, setMasFiltros] = useState(false);
  const [filtrosMobileAbiertos, setFiltrosMobileAbiertos] = useState(false);
  const catalogoRef = useRef(null);
  const montadoRef = useRef(false); // evita resetear la página en el montaje inicial
  const filtrosMobileRef = useRef(false);  // espejo del estado para el scroll handler
  const scrollAlAbrirRef = useRef(0);      // scrollY estabilizado tras abrir el panel
  const ignorarScrollRef = useRef(false);  // true durante 600ms después de abrir (evita cierre por layout shift)
  const [operacion, setOperacion] = useState(() => sp('op'));
  const [tipo, setTipo] = useState(() => sp('tipo', 'Todos'));
  const [precioMin, setPrecioMin] = useState(() => sp('pmin'));
  const [precioMax, setPrecioMax] = useState(() => sp('pmax'));
  const [monedaBusqueda, setMonedaBusqueda] = useState(() => sp('moneda', 'USD'));

  // ── Cotización dólar ──
  const [cotizacion, setCotizacion] = useState(null); // { blue, oficial }
  useEffect(() => {
    fetch('https://api.bluelytics.com.ar/v2/latest')
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => {
        const blue    = d?.blue?.value_sell;
        const oficial = d?.oficial?.value_sell;
        if (typeof blue === 'number' && typeof oficial === 'number' && blue > 0 && oficial > 0) {
          setCotizacion({ blue, oficial });
        }
      })
      .catch(() => {}); // falla silenciosa — filtros siguen funcionando en moneda original
  }, []);
  const [dormitorios, setDormitorios] = useState(() => sp('dorm'));
  const [m2Min, setM2Min] = useState(() => sp('m2min'));
  const [m2Max, setM2Max] = useState(() => sp('m2max'));
  const [conCochera, setConCochera] = useState(() => sp('cochera') === '1');
  const [aptoCredito, setAptoCredito] = useState(() => sp('credito') === '1');
  const [aptoCreditoSinPlano, setAptoCreditoSinPlano] = useState(() => sp('creditosp') === '1');
  const [calle, setCalle] = useState(() => sp('calle'));
  const [provincia, setProvincia] = useState(() => sp('prov'));
  const [ciudad, setCiudad] = useState(() => sp('ciudad'));
  const [barrios, setBarrios] = useState(() => sp('barrios') ? sp('barrios').split('|') : []);
  const [dropdownBarrioAbierto, setDropdownBarrioAbierto] = useState(false);

  // ── Geolocalización automática ──
  const [toastGeo, setToastGeo] = useState(null);
  const palabrasGeoRef = useRef(null); // palabras del nombre geo pendientes de match
  const geoAplicadoRef = useRef(false); // evita aplicar dos veces

  useEffect(() => {
    if (sp('ciudad') || sp('prov')) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json&accept-language=es`, { headers: { 'User-Agent': 'FormoProp/1.0 (formoprop.com.ar)' } })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (!data) return;
            const estado = data.address?.state?.toLowerCase() || '';
            if (!estado.includes('formosa')) return;
            const raw = [data.address?.city, data.address?.town, data.address?.village, data.address?.suburb, data.address?.county]
              .filter(Boolean).join(' ').toLowerCase();
            palabrasGeoRef.current = raw;
          })
          .catch(() => {});
      },
      () => {}
    );
  }, []); // eslint-disable-line

  // ── Búsqueda guardada ──
  const [modalGuardar, setModalGuardar] = useState(false);
  const [emailGuardar, setEmailGuardar] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [guardadoError, setGuardadoError] = useState(null);

  // Función para abrir/cerrar el panel de filtros en mobile
  const toggleFiltrosMobile = () => {
    const nuevoValor = !filtrosMobileRef.current;
    filtrosMobileRef.current = nuevoValor;
    setFiltrosMobileAbiertos(nuevoValor);
  };


  useEffect(() => {
    // 1) Pintar al instante lo cacheado (aunque esté vencido).
    const cached = getCachePropiedades();
    if (cached) {
      setPropiedades(cached);
      setCargando(false);
    }
    // 2) Si el caché es fresco, no molestamos a Firestore.
    if (cacheEsFresco()) return;
    // 3) Refrescar en segundo plano (stale-while-revalidate).
    getDocs(query(collection(db, "propiedades"), orderBy("fecha", "desc")))
      .then(snap => {
        const props = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        setCachePropiedades(props);
        setPropiedades(props);
        setCargando(false);
      })
      .catch(err => {
        console.error(err);
        // Si ya mostramos algo del caché, no rompemos la vista por un fallo de red.
        if (!cached) setError("No se pudieron cargar las propiedades. Intentá de nuevo más tarde.");
        setCargando(false);
      });
  }, []);

  const limpiarFiltros = () => { setOperacion(''); setTipo('Todos'); setPrecioMin(''); setPrecioMax(''); setDormitorios(''); setM2Min(''); setM2Max(''); setConCochera(false); setAptoCredito(false); setAptoCreditoSinPlano(false); setCalle(''); setProvincia(''); setCiudad(''); setBarrios([]); setMonedaBusqueda('USD'); setOrden('mezcla'); };

  // Sincronizar filtros → URL (para búsquedas compartibles)
  useEffect(() => {
    const params = {};
    if (operacion)          params.op     = operacion;
    if (tipo !== 'Todos')   params.tipo   = tipo;
    if (precioMin)          params.pmin   = precioMin;
    if (precioMax)          params.pmax   = precioMax;
    if (monedaBusqueda !== 'USD') params.moneda = monedaBusqueda;
    if (dormitorios)        params.dorm   = dormitorios;
    if (ciudad)             params.ciudad = ciudad;
    if (conCochera)          params.cochera = '1';
    if (aptoCredito)         params.credito = '1';
    if (aptoCreditoSinPlano) params.creditosp = '1';
    if (calle.trim())        params.calle = calle.trim();
    if (provincia)           params.prov   = provincia;
    if (barrios.length > 0) params.barrios = barrios.join('|');
    if (m2Min)               params.m2min  = m2Min;
    if (m2Max)               params.m2max  = m2Max;
    if (orden !== 'mezcla') params.orden = orden;
    if (pagina > 1)          params.pagina = pagina;
    setSearchParams(params, { replace: true });
  }, [operacion, tipo, precioMin, precioMax, monedaBusqueda, dormitorios, m2Min, m2Max, conCochera, aptoCredito, aptoCreditoSinPlano, calle, provincia, ciudad, barrios, orden, pagina]); // eslint-disable-line

  // Cerrar dropdown de barrios al hacer clic afuera
  const refDropdownBarrio = useRef(null);
  useEffect(() => {
    if (!dropdownBarrioAbierto) return;
    const handler = (e) => {
      if (refDropdownBarrio.current && !refDropdownBarrio.current.contains(e.target)) {
        setDropdownBarrioAbierto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownBarrioAbierto]);

  // Convierte el precio de una propiedad a la moneda de búsqueda usando cotiz. blue
  const normalizarPrecio = (propiedad, monedaDestino) => {
    const p = propiedad.precio || 0;
    const m = propiedad.moneda || 'USD';
    const tasa = cotizacion?.blue;
    if (!tasa || m === monedaDestino) return p;
    if (monedaDestino === 'USD' && m === 'ARS') return p / tasa;   // ARS → USD
    if (monedaDestino === 'ARS' && m === 'USD') return p * tasa;   // USD → ARS
    return p;
  };

  // Provincias únicas presentes en el catálogo (arma el select solo)
  const provinciasDisponibles = (() => {
    const set = new Set(propiedades.filter(estaVigente).map(p => p.provincia).filter(Boolean));
    if (provincia && !set.has(provincia)) set.add(provincia); // conservar la elegida por URL aunque no tenga props ahora
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  })();

  // Ciudades y barrios únicos para los selects
  const ciudadesDisponibles = [...new Set(
    propiedades
      .filter(p => estaVigente(p) && (!provincia || p.provincia?.toLowerCase() === provincia.toLowerCase()))
      .map(p => p.ciudad).filter(Boolean)
  )].sort();

  // Aplicar ciudad geo una vez que las ciudades del catálogo están disponibles
  useEffect(() => {
    if (geoAplicadoRef.current) return;
    if (!palabrasGeoRef.current) return;
    // Obtener todas las ciudades de Formosa del catálogo (sin filtro de provincia aún)
    const todasCiudades = [...new Set(propiedades.filter(p => estaVigente(p) && p.provincia?.toLowerCase() === 'formosa').map(p => p.ciudad).filter(Boolean))];
    if (todasCiudades.length === 0) return;
    const palabras = palabrasGeoRef.current;
    const sinAcento = s => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    const match = todasCiudades.find(c => sinAcento(palabras).includes(sinAcento(c.toLowerCase())));
    if (!match) return;
    geoAplicadoRef.current = true;
    setProvincia('Formosa');
    setCiudad(match);
    setToastGeo(`📍 Mostrando propiedades en ${match}`);
  }, [propiedades]); // eslint-disable-line

  const barriosDisponibles = [...new Set(
    propiedades
      .filter(p => estaVigente(p) && (!ciudad || p.ciudad?.toLowerCase() === ciudad.toLowerCase()))
      .map(p => p.barrio).filter(Boolean)
  )].sort();

  const propiedadesFiltradas = propiedades.filter(p => {
    if (!estaVigente(p)) return false;
    if (soloFavoritos && !esFavorito(p.id)) return false;
    if (operacion !== "" && p.operacion !== operacion) return false;
    if (tipo === "Otro") {
      // "Otro" = cualquier tipo que no esté en la lista canónica del combo
      if (TIPOS_CONOCIDOS.includes(p.tipoPropiedad)) return false;
    } else if (tipo !== "Todos" && p.tipoPropiedad !== tipo) return false;
    if (precioMin !== "" || precioMax !== "") {
      const pNorm = normalizarPrecio(p, monedaBusqueda);
      if (precioMin !== "" && pNorm < parseFloat(precioMin)) return false;
      if (precioMax !== "" && pNorm > parseFloat(precioMax)) return false;
    }
    if (dormitorios !== "" && (!p.habitaciones || parseInt(p.habitaciones) < parseInt(dormitorios))) return false;
    if (m2Min !== "" && (!p.m2Totales || parseFloat(p.m2Totales) < parseFloat(m2Min))) return false;
    if (m2Max !== "" && (!p.m2Totales || parseFloat(p.m2Totales) > parseFloat(m2Max))) return false;
    if (conCochera && !(parseInt(p.cocheras) > 0)) return false;
    if (aptoCredito && !esAptoCredito(p)) return false;
    if (aptoCreditoSinPlano && !esAptoCreditoSinPlano(p)) return false;
    if (calle.trim()) {
      const norm = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
      if (!norm(p.direccion).includes(norm(calle))) return false;
    }
    if (provincia !== "" && p.provincia?.toLowerCase() !== provincia.toLowerCase()) return false;
    if (ciudad !== "" && p.ciudad?.toLowerCase() !== ciudad.toLowerCase()) return false;
    if (barrios.length > 0 && !barrios.some(b => p.barrio?.toLowerCase().includes(b.toLowerCase()))) return false;
    return true;
  });

  const hayFiltros = operacion !== '' || tipo !== 'Todos' || precioMin !== '' || precioMax !== '' || dormitorios !== '' || m2Min !== '' || m2Max !== '' || conCochera || aptoCredito || aptoCreditoSinPlano || calle.trim() !== '' || provincia !== '' || ciudad !== '' || barrios.length > 0;
  const filtrosSecundariosActivos = [dormitorios !== '', m2Min !== '' || m2Max !== '', conCochera, aptoCredito, aptoCreditoSinPlano, calle.trim() !== '', provincia !== '', ciudad !== '', barrios.length > 0].filter(Boolean).length;
  const cantFiltrosActivos = [operacion !== '', tipo !== 'Todos', precioMin !== '', precioMax !== '', dormitorios !== '', m2Min !== '' || m2Max !== '', conCochera, aptoCredito, aptoCreditoSinPlano, calle.trim() !== '', provincia !== '', ciudad !== '', barrios.length > 0].filter(Boolean).length;
  const resumenFiltros = [
    operacion === 'Venta' ? 'Comprar' : operacion === 'Alquiler' ? 'Alquilar' : null,
    tipo !== 'Todos' ? tipo : null,
    ciudad || null,
  ].filter(Boolean).slice(0, 2);

  const toDate = (f) => f?.toDate ? f.toDate() : (f ? new Date(f) : new Date(0));
  const propiedadesOrdenadas = [...propiedadesFiltradas].sort((a, b) => {
    if (orden === 'precio-asc')  return normalizarPrecio(a, 'USD') - normalizarPrecio(b, 'USD');
    if (orden === 'precio-desc') return normalizarPrecio(b, 'USD') - normalizarPrecio(a, 'USD');
    if (orden === 'reciente')    return toDate(b.fecha) - toDate(a.fecha);
    // 'mezcla' (default): destacadas primero (en orden aleatorio), luego el resto (aleatorio)
    const da = a.destacada ? 1 : 0, db = b.destacada ? 1 : 0;
    if (da !== db) return db - da;
    return hashOrden(a.id) - hashOrden(b.id);
  });

  const totalPaginas = Math.ceil(propiedadesOrdenadas.length / porPagina);
  const propiedadesPagina = propiedadesOrdenadas.slice((pagina - 1) * porPagina, pagina * porPagina);

  // Cambiar el tamaño de página: persiste la elección, vuelve a la página 1
  // (si no, podrías quedar en una página que ya no existe) y sube al catálogo.
  const cambiarPorPagina = (n) => {
    setPorPagina(n);
    setPagina(1);
    try { localStorage.setItem('porPaginaCatalogo', String(n)); } catch {}
    catalogoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Resetear a página 1 cuando cambian filtros u orden
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Resetear a la página 1 SOLO cuando un filtro realmente cambia.
    // (Comparar contra el valor previo evita el reset en el montaje y soporta el
    //  doble-render de StrictMode, así se respeta la página restaurada desde la URL.)
    const claveFiltros = JSON.stringify([operacion, tipo, precioMin, precioMax, dormitorios, m2Min, m2Max, conCochera, aptoCredito, aptoCreditoSinPlano, calle, provincia, ciudad, barrios, orden]);
    if (montadoRef.current === false) { montadoRef.current = claveFiltros; return; }
    if (montadoRef.current !== claveFiltros) { montadoRef.current = claveFiltros; setPagina(1); }
  }, [operacion, tipo, precioMin, precioMax, dormitorios, m2Min, m2Max, conCochera, aptoCredito, aptoCreditoSinPlano, calle, provincia, ciudad, barrios, orden]);

  // Abrir filtros secundarios si vienen activos en la URL
  useEffect(() => {
    if (dormitorios || m2Min || m2Max || conCochera || aptoCredito || aptoCreditoSinPlano || calle || provincia || ciudad || barrios.length > 0) setMasFiltros(true);
  }, []); // eslint-disable-line

  const formosaCenter = [-26.1848, -58.1731];

  // ── Guardar búsqueda ──
  const abrirGuardar = () => { setEmailGuardar(''); setGuardadoOk(false); setGuardadoError(null); setModalGuardar(true); };
  const guardarBusqueda = async (e) => {
    e.preventDefault();
    setGuardadoError(null);
    if (!emailGuardar.trim()) { setGuardadoError('Ingresá tu email.'); return; }
    setGuardando(true);
    try {
      const token = crypto.randomUUID();
      await addDoc(collection(db, 'busquedasGuardadas'), {
        email: emailGuardar.trim().toLowerCase(),
        token,
        operacion: operacion || '',
        tipo: tipo !== 'Todos' ? tipo : '',
        conCochera: conCochera || false,
        aptoCredito: aptoCredito || false,
        aptoCreditoSinPlano: aptoCreditoSinPlano || false,
        provincia: provincia || '',
        ciudad: ciudad || '',
        barrio: barrios.join(','),
        dormitorios: dormitorios || '',
        m2Min: m2Min || '',
        m2Max: m2Max || '',
        precioMin: precioMin || '',
        precioMax: precioMax || '',
        moneda: monedaBusqueda || 'USD',
        activa: true,
        creadoEn: new Date(),
      });
      setGuardadoOk(true);
    } catch {
      setGuardadoError('Error al guardar. Intentá de nuevo.');
    }
    setGuardando(false);
  };

  return (
    <>
      {/* ── MODAL GUARDAR BÚSQUEDA ── */}
      {modalGuardar && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={e => { if (e.target === e.currentTarget) setModalGuardar(false); }}>
          <div style={{ background:'white', borderRadius:'14px', width:'100%', maxWidth:'420px', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 24px', borderBottom:'1px solid #eee' }}>
              <h2 style={{ margin:0, fontSize:'18px' }}>🔔 Guardar búsqueda</h2>
              <button onClick={() => setModalGuardar(false)} style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#888' }}>✕</button>
            </div>
            <div style={{ padding:'24px' }}>
              {guardadoOk ? (
                <div style={{ textAlign:'center', padding:'16px 0' }}>
                  <p style={{ fontSize:'40px', margin:'0 0 12px' }}>✅</p>
                  <p style={{ fontWeight:'bold', fontSize:'16px', color:'#155724', margin:'0 0 8px' }}>¡Búsqueda guardada!</p>
                  <p style={{ color:'#555', fontSize:'14px', margin:'0 0 14px', lineHeight:1.6 }}>
                    Te avisaremos a <strong>{emailGuardar}</strong> cuando haya propiedades nuevas que coincidan con tus filtros.
                  </p>
                  <div style={{ background:'#fff8e1', border:'1px solid #ffe0a3', borderRadius:'8px', padding:'12px 14px', margin:'0 0 20px', fontSize:'13px', color:'#92600a', lineHeight:1.55, textAlign:'left' }}>
                    📩 <strong>Importante:</strong> revisá tu carpeta de <strong>Spam/Correo no deseado</strong>. Si el primer aviso cae ahí, marcalo como <strong>"No es spam"</strong> para recibir los próximos en tu bandeja de entrada.
                  </div>
                  <button onClick={() => setModalGuardar(false)} style={{ padding:'11px 28px', background:'#1a1a2e', color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontWeight:'bold', fontSize:'14px' }}>Cerrar</button>
                </div>
              ) : (
                <form onSubmit={guardarBusqueda}>
                  {/* Resumen de filtros activos */}
                  <div style={{ background:'#f8f9fa', borderRadius:'8px', padding:'12px 14px', marginBottom:'18px', fontSize:'13px', color:'#555', lineHeight:1.7 }}>
                    <p style={{ margin:'0 0 4px', fontWeight:'700', fontSize:'12px', color:'#888', textTransform:'uppercase', letterSpacing:'0.5px' }}>Filtros activos</p>
                    {operacion && <span style={{ marginRight:8 }}>· {operacion}</span>}
                    {tipo !== 'Todos' && <span style={{ marginRight:8 }}>· {tipo}</span>}
                    {conCochera && <span style={{ marginRight:8 }}>· Con cochera</span>}
                    {aptoCredito && <span style={{ marginRight:8 }}>· Apto crédito</span>}
                    {aptoCreditoSinPlano && <span style={{ marginRight:8 }}>· Apto crédito sin plano</span>}
                    {calle.trim() && <span style={{ marginRight:8 }}>· Calle: {calle.trim()}</span>}
                    {provincia && <span style={{ marginRight:8 }}>· {provincia}</span>}
                    {ciudad && <span style={{ marginRight:8 }}>· {ciudad}</span>}
                    {barrios.length > 0 && <span style={{ marginRight:8 }}>· Barrio{barrios.length > 1 ? 's' : ''}: {barrios.join(', ')}</span>}
                    {dormitorios && <span style={{ marginRight:8 }}>· {dormitorios}+ dorm.</span>}
                    {(m2Min || m2Max) && <span style={{ marginRight:8 }}>· {m2Min || '0'} – {m2Max || '∞'} m²</span>}
                    {(precioMin || precioMax) && <span style={{ marginRight:8 }}>· {monedaBusqueda} {precioMin || '0'} – {precioMax || '∞'}</span>}
                    {!operacion && tipo === 'Todos' && !ciudad && barrios.length === 0 && !dormitorios && !m2Min && !m2Max && !precioMin && !precioMax && <span style={{ color:'#aaa', fontStyle:'italic' }}>Sin filtros (todas las propiedades nuevas)</span>}
                  </div>
                  <label style={{ display:'block', fontSize:'12px', fontWeight:'bold', color:'#555', marginBottom:'4px' }}>Tu email *</label>
                  <input
                    type="email" value={emailGuardar} onChange={e => setEmailGuardar(e.target.value)}
                    placeholder="tu@email.com" required autoFocus
                    style={{ width:'100%', padding:'11px 13px', marginBottom:'12px', borderRadius:'6px', border:'1px solid #ddd', boxSizing:'border-box', fontSize:'14px' }}
                  />
                  <p style={{ margin:'0 0 18px', fontSize:'12px', color:'#888', lineHeight:1.5 }}>
                    Te notificaremos cuando se publiquen propiedades que coincidan. Podés cancelar la suscripción en cualquier momento con un clic desde el email.
                  </p>
                  {guardadoError && <p style={{ color:'#dc3545', background:'#fff5f5', border:'1px solid #f5c2c7', borderRadius:'6px', padding:'9px 13px', fontSize:'13px', margin:'0 0 10px' }}>{guardadoError}</p>}
                  <div style={{ display:'flex', gap:'10px' }}>
                    <button type="submit" disabled={guardando} style={{ flex:1, padding:'12px', background:'#1a1a2e', color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontWeight:'bold', fontSize:'14px' }}>
                      {guardando ? 'Guardando...' : '🔔 Guardar búsqueda'}
                    </button>
                    <button type="button" onClick={() => setModalGuardar(false)} style={{ padding:'12px 18px', background:'#6c757d', color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'14px' }}>Cancelar</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .prop-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .prop-card:hover { transform: translateY(-5px); box-shadow: 0 16px 40px rgba(0,0,0,0.14) !important; }
        .filtro-pill { transition: all 0.15s ease; }
        .filtro-pill:hover { border-color: #007bff !important; color: #007bff !important; }
        /* Barra compacta mobile: oculta en desktop */
        .filtro-mobile-toggle { display: none; }
        /* Panel expandible: siempre visible en desktop */
        .filtros-expandible { display: block; }
        /* Header, footer y títulos de grupo del panel: solo en mobile (estilo Zonaprop) */
        .fp-filtros-header, .fp-filtros-footer, .fp-grupo { display: none; }
        @media (max-width: 900px) {
          .hero-titulo { font-size: 26px !important; }
          .filtros-inner { gap: 8px !important; }
          .sep-vertical { display: none !important; }
          .grid-props { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)) !important; }
        }
        .grid-grilla { grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)) !important; gap: 14px !important; }
        .grid-props, .grid-grilla { width: 100%; }
        .grid-props > *, .grid-grilla > * { min-width: 0; }
        .grid-props img, .grid-grilla img { max-width: 100%; }
        @media (max-width: 600px) {
          .hero-section { padding: 36px 20px !important; }
          .hero-titulo { font-size: 22px !important; }
          .hero-sub { font-size: 13px !important; }
          .grid-grilla { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
          /* Barra compacta siempre visible en mobile */
          .filtro-mobile-toggle {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            padding: 9px 14px;
            gap: 8px;
            border-bottom: 1px solid #f0f0f0;
          }
          /* Panel de filtros: en mobile se abre a pantalla completa (estilo Zonaprop) */
          .filtros-expandible { display: none !important; }
          .filtros-expandible.abierto {
            display: block !important;
            position: fixed; inset: 0; z-index: 3000;
            background: white; overflow-y: auto;
            padding-bottom: 78px; /* espacio para el footer fijo */
            animation: fpSlideUp 0.2s ease;
          }
          @keyframes fpSlideUp {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .fp-filtros-header {
            display: flex !important; align-items: center; justify-content: space-between;
            position: sticky; top: 0; z-index: 1; background: white;
            padding: 14px 16px; border-bottom: 1px solid #eee;
          }
          .fp-filtros-footer {
            display: flex !important; gap: 10px; align-items: center;
            position: fixed; bottom: 0; left: 0; right: 0; z-index: 3001;
            background: white; border-top: 1px solid #eee; padding: 10px 14px;
            box-shadow: 0 -2px 10px rgba(0,0,0,0.08);
          }
          .filtros-barra { padding: 10px 14px !important; }
          .filtros-inner { flex-direction: column !important; align-items: stretch !important; }
          .ops-pills { justify-content: center; }
          .filtros-secundarios { flex-direction: column !important; gap: 8px !important; }
          .filtros-secundarios select,
          .filtros-secundarios input:not([type="checkbox"]) { width: 100% !important; }
          /* Títulos de grupo visibles en mobile */
          .fp-grupo {
            display: block !important;
            font-size: 12px; font-weight: 800; color: #6b7280;
            text-transform: uppercase; letter-spacing: 0.4px;
            padding: 16px 2px 6px; width: 100%;
          }
          /* En pantalla completa mostramos todo: el sub-botón "Más filtros" no hace falta */
          .mas-filtros-btn { display: none !important; }
          /* Bloque de filtros adicionales: campos a todo el ancho en mobile */
          .bloque-secundario > div { flex-direction: column !important; align-items: stretch !important; }
          .bloque-secundario select, .bloque-secundario input:not([type="checkbox"]) { width: 100% !important; }
          .solo-desktop { display: none !important; }
          /* Barrios: en mobile el desplegable va en el flujo (no flotante sobre el footer) */
          .fp-barrio-panel {
            position: static !important;
            width: 100% !important; min-width: 0 !important;
            box-shadow: none !important;
            margin-top: 6px;
          }
          .fp-barrio-panel > div[style*="overflow"] { max-height: 260px !important; }
          /* El vista-contador está en la barra compacta mobile, no dentro del panel */
          .vista-contador { display: none !important; }
          .grid-props { grid-template-columns: 1fr !important; }
          .contenido-padding { padding: 20px 16px !important; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div className="hero-section" style={{
        background: `linear-gradient(rgba(13,27,42,0.70), rgba(13,27,42,0.80)), url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1920&q=80') center/cover no-repeat`,
        padding: '64px 30px', textAlign: 'center',
      }}>
        <h1 className="hero-titulo" style={{ color: 'white', fontSize: '36px', margin: '0 0 14px 0', fontWeight: '900', lineHeight: 1.2 }}>
          {config.homeTitulo}
        </h1>
        <p className="hero-sub" style={{ color: '#cbd5e1', margin: '0 auto 28px', fontSize: '15px', maxWidth: '500px' }}>
          {config.homeSubtitulo}
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { label: '🔍 Comprar', op: 'Venta' },
            { label: '🏠 Alquilar', op: 'Alquiler' },
          ].map(({ label, op }) => (
            <button key={op} onClick={() => {
              setOperacion(op);
              if (op === 'Alquiler') { setMonedaBusqueda('ARS'); setPrecioMin(''); setPrecioMax(''); }
              if (op === 'Venta')    { setMonedaBusqueda('USD'); setPrecioMin(''); setPrecioMax(''); }
              catalogoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }} style={{
              padding: '11px 26px', cursor: 'pointer',
              background: 'rgba(255,255,255,0.13)', border: '1.5px solid rgba(255,255,255,0.55)',
              color: 'white', borderRadius: '8px', fontWeight: '700', fontSize: '14px',
            }}>
              {label}
            </button>
          ))}
          <Link to="/vender" style={{
            padding: '11px 26px',
            background: 'rgba(255,255,255,0.13)', border: '1.5px solid rgba(255,255,255,0.55)',
            color: 'white', borderRadius: '8px', textDecoration: 'none',
            fontWeight: '700', fontSize: '14px',
          }}>
            🏷️ Vender →
          </Link>
        </div>
      </div>

      {/* ── FRANJA PRÓXIMOS REMATES (se oculta sola si no hay) ── */}
      <FranjaRemates />

      {/* ── BARRA DE FILTROS ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

        {/* ── BARRA COMPACTA MOBILE (solo visible ≤600px) ── */}
        <div className="filtro-mobile-toggle">
          {/* Izquierda: botón filtros + chips resumen */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <button
              onClick={toggleFiltrosMobile}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0,
                padding: '6px 12px', borderRadius: '20px', border: '2px solid',
                borderColor: cantFiltrosActivos > 0 ? '#007bff' : '#d1d5db',
                background: cantFiltrosActivos > 0 ? '#eff6ff' : 'white',
                color: cantFiltrosActivos > 0 ? '#007bff' : '#374151',
                fontWeight: '700', fontSize: '13px', cursor: 'pointer',
              }}
            >
              {filtrosMobileAbiertos ? '▲' : '▼'} Filtros
              {cantFiltrosActivos > 0 && (
                <span style={{ background: '#007bff', color: 'white', borderRadius: '50%', width: '17px', height: '17px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800' }}>
                  {cantFiltrosActivos}
                </span>
              )}
            </button>
            {/* Chips con los filtros activos */}
            {resumenFiltros.map(f => (
              <span key={f} style={{ background: '#eff6ff', color: '#3b82f6', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '78px', flexShrink: 0 }}>
                {f}
              </span>
            ))}
          </div>
          {/* Derecha: conteo + orden + vista (mobile) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
              <strong style={{ color: '#111' }}>{propiedadesOrdenadas.length}</strong> prop.
            </span>
            <select value={orden} onChange={e => setOrden(e.target.value)} style={{ ...selectFiltro, fontSize: '11px', padding: '5px 6px' }}>
              <option value="mezcla">Destacadas</option>
              <option value="reciente">Recientes</option>
              <option value="precio-asc">$ ↑</option>
              <option value="precio-desc">$ ↓</option>
            </select>
            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px' }}>
              {[['lista', '▤'], ['grilla', '⊞'], ['mapa', '🗺️']].map(([v, ic]) => (
                <button key={v} onClick={() => setVista(v)} style={{
                  padding: '4px 8px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
                  background: vista === v ? 'white' : 'transparent',
                  boxShadow: vista === v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                }}>{ic}</button>
              ))}
            </div>
          </div>
        </div>

        {/* ── FILTROS COMPLETOS (desktop: siempre; mobile: pantalla completa) ── */}
        <div className={`filtros-expandible${filtrosMobileAbiertos ? ' abierto' : ''}`}>

          {/* Header (solo mobile) */}
          <div className="fp-filtros-header">
            <span style={{ fontSize: '17px', fontWeight: '800', color: '#111' }}>Filtros</span>
            <button onClick={toggleFiltrosMobile} aria-label="Cerrar filtros"
              style={{ background: 'none', border: 'none', fontSize: '22px', lineHeight: 1, cursor: 'pointer', color: '#374151', padding: '0 4px' }}>✕</button>
          </div>

          {/* Fila principal */}
          <div className="filtros-barra" style={{ padding: '12px 30px' }}>
            <div className="filtros-inner" style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>

              {/* Pills operación */}
              <div className="fp-grupo">Operación</div>
              <div className="ops-pills" style={{ display: 'flex', gap: '6px' }}>
                {[['Venta', 'Comprar'], ['Alquiler', 'Alquilar']].map(([val, label]) => {
                  // Ocultar el otro botón cuando hay uno seleccionado
                  if (operacion && operacion !== val) return null;
                  return (
                    <button key={val} className="filtro-pill"
                      onClick={() => {
                        const nuevo = operacion === val ? '' : val;
                        setOperacion(nuevo);
                        // Al elegir Alquiler → moneda ARS; al elegir Venta → moneda USD
                        if (nuevo === 'Alquiler') { setMonedaBusqueda('ARS'); setPrecioMin(''); setPrecioMax(''); }
                        if (nuevo === 'Venta')    { setMonedaBusqueda('USD'); setPrecioMin(''); setPrecioMax(''); }
                      }}
                      style={{
                        padding: '7px 16px', borderRadius: '20px', border: '2px solid',
                        borderColor: operacion === val ? '#007bff' : '#d1d5db',
                        background: operacion === val ? '#007bff' : 'white',
                        color: operacion === val ? 'white' : '#374151',
                        fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>{label}</button>
                  );
                })}
              </div>

              <div className="sep-vertical" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />

              {/* Tipo */}
              <div className="fp-grupo">Tipo de propiedad</div>
              <select value={tipo} onChange={e => setTipo(e.target.value)} style={selectFiltro}>
                <option value="Todos">Tipo de propiedad</option>
                <option value="Casa">Casa</option>
                <option value="Casa Quinta">Casa Quinta</option>
                <option value="Campo">Campo</option>
                <option value="Departamento">Departamento</option>
                <option value="Complejo de departamentos">Complejo de departamentos</option>
                <option value="Terreno">Terreno</option>
                <option value="Local">Local</option>
                <option value="Oficina">Oficina</option>
                <option value="Galpón">Galpón</option>
                <option value="Otro">Otro</option>
              </select>

              <div className="sep-vertical" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />

              {/* Precio */}
              <div className="fp-grupo">Precio</div>
              <div className="filtros-secundarios" style={{ display: 'flex', gap: '8px', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Selector de moneda */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                  {['USD', 'ARS'].map(m => (
                    <button key={m} onClick={() => { setMonedaBusqueda(m); setPrecioMin(''); setPrecioMax(''); }}
                      style={{ padding: '5px 10px', borderRadius: '6px', border: '1.5px solid', cursor: 'pointer', fontSize: '12px', fontWeight: '700', transition: 'all 0.15s',
                        borderColor: monedaBusqueda === m ? '#007bff' : '#d1d5db',
                        background: monedaBusqueda === m ? '#007bff' : 'white',
                        color: monedaBusqueda === m ? 'white' : '#374151',
                      }}>
                      {m === 'USD' ? 'U$S' : '$'}
                    </button>
                  ))}
                </div>
                <input
                  type="text" inputMode="numeric"
                  placeholder="Desde"
                  value={precioMin ? parseInt(precioMin).toLocaleString('es-AR') : ''}
                  onChange={e => setPrecioMin(e.target.value.replace(/\D/g, ''))}
                  style={{ ...selectFiltro, width: '110px' }}
                />
                <input
                  type="text" inputMode="numeric"
                  placeholder="Hasta"
                  value={precioMax ? parseInt(precioMax).toLocaleString('es-AR') : ''}
                  onChange={e => setPrecioMax(e.target.value.replace(/\D/g, ''))}
                  style={{ ...selectFiltro, width: '110px' }}
                />
                {/* Badge cotización */}
                {cotizacion && (
                  <span title={`Oficial: $${cotizacion.oficial.toLocaleString('es-AR')}`}
                    style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', background: '#f3f4f6', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: 'default' }}>
                    🔵 Blue: ${cotizacion.blue.toLocaleString('es-AR')}
                  </span>
                )}

              </div>

              <div className="sep-vertical" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />

              {/* Más filtros */}
              <button
                className="mas-filtros-btn"
                onClick={() => setMasFiltros(v => !v)}
                title="Dormitorios · m² · Cochera · Provincia · Ciudad · Barrio"
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: '1.5px solid',
                  borderColor: masFiltros || filtrosSecundariosActivos > 0 ? '#007bff' : '#d1d5db',
                  background: masFiltros ? '#eff6ff' : 'white',
                  color: masFiltros || filtrosSecundariosActivos > 0 ? '#007bff' : '#374151',
                  fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                  transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                {masFiltros ? '▲' : '▼'} Más filtros
                {filtrosSecundariosActivos > 0 && (
                  <span style={{ background: '#007bff', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800', flexShrink: 0 }}>
                    {filtrosSecundariosActivos}
                  </span>
                )}
              </button>

              <div className="sep-vertical" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />

              {/* Avisame + Limpiar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <button onClick={abrirGuardar} title="Guardar esta búsqueda y recibir alertas por email"
                  style={{ padding:'7px 13px', borderRadius:'20px', border:'1.5px solid #d1d5db', background:'white', color:'#374151', fontWeight:'600', fontSize:'12px', cursor:'pointer', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'5px', transition:'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='#007bff'; e.currentTarget.style.color='#007bff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='#d1d5db'; e.currentTarget.style.color='#374151'; }}>
                  🔔✉️ Notificarme
                </button>
                {hayFiltros && (
                  <button onClick={limpiarFiltros} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                    Limpiar
                  </button>
                )}
              </div>

              {/* Contador + orden + favoritos — solo visible en desktop */}
              <div className="vista-contador" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, marginLeft: 'auto' }}>
                <button
                  onClick={() => cantFav > 0 && setSoloFavoritos(v => !v)}
                  title={cantFav === 0 ? 'Guardá propiedades con ♡ para verlas aquí' : soloFavoritos ? 'Ver todas' : `Ver mis ${cantFav} favorita${cantFav !== 1 ? 's' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', borderRadius: '20px', border: '2px solid', borderColor: soloFavoritos ? '#e11d48' : cantFav > 0 ? '#fda4af' : '#e5e7eb', background: soloFavoritos ? '#fff1f2' : 'white', color: soloFavoritos ? '#e11d48' : cantFav > 0 ? '#fb7185' : '#9ca3af', fontWeight: '700', fontSize: '12px', cursor: cantFav > 0 ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                >
                  {cantFav > 0 ? (soloFavoritos ? '♥' : '♡') : '♡'} Favoritas{cantFav > 0 ? ` (${cantFav})` : ''}
                </button>
                <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  <strong style={{ color: '#111' }}>{propiedadesOrdenadas.length}</strong> propiedades
                </span>
                <select value={orden} onChange={e => setOrden(e.target.value)} style={{ ...selectFiltro, fontSize: '12px', padding: '6px 10px' }}>
                  <option value="mezcla">Destacadas</option>
                  <option value="reciente">Más recientes</option>
                  <option value="precio-asc">Precio: menor a mayor</option>
                  <option value="precio-desc">Precio: mayor a menor</option>
                </select>
                <div className="sep-vertical" style={{ width: '1px', height: '28px', background: '#e5e7eb', flexShrink: 0 }} />
                {/* Toggle de vista — pegado a la derecha */}
                <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '3px' }}>
                  {[['lista', '▤'], ['grilla', '⊞'], ['mapa', '🗺️']].map(([v, ic]) => (
                    <button key={v} onClick={() => setVista(v)} title={v} style={{
                      padding: '5px 10px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px',
                      background: vista === v ? 'white' : 'transparent',
                      boxShadow: vista === v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                      transition: 'all 0.15s',
                    }}>{ic}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Panel expandible: Más Filtros (en mobile siempre visible dentro del overlay) */}
          {(masFiltros || filtrosMobileAbiertos) && (
            <div className="bloque-secundario" style={{ borderTop: '1px solid #f0f0f0', background: '#fafafa', padding: '12px 30px' }}>
              <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="fp-grupo">Características</span>
                <span className="solo-desktop" style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginRight: '4px' }}>Filtros adicionales:</span>
                <select value={dormitorios} onChange={e => setDormitorios(e.target.value)} style={{ ...selectFiltro, minWidth: '130px' }}>
                  <option value="">Dormitorios</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                  <option value="5">5+</option>
                </select>
                <input
                  type="text" inputMode="numeric"
                  placeholder="m² desde"
                  value={m2Min}
                  onChange={e => setM2Min(e.target.value.replace(/\D/g, ''))}
                  style={{ ...selectFiltro, width: '90px' }}
                />
                <input
                  type="text" inputMode="numeric"
                  placeholder="m² hasta"
                  value={m2Max}
                  onChange={e => setM2Max(e.target.value.replace(/\D/g, ''))}
                  style={{ ...selectFiltro, width: '90px' }}
                />
                <button
                  onClick={() => setConCochera(v => !v)}
                  style={{
                    padding: '7px 14px', borderRadius: '20px', border: '2px solid', cursor: 'pointer',
                    borderColor: conCochera ? '#007bff' : '#d1d5db',
                    background: conCochera ? '#007bff' : 'white',
                    color: conCochera ? 'white' : '#374151',
                    fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                >
                  🚗 Con cochera
                </button>
                <button
                  onClick={() => setAptoCredito(v => !v)}
                  style={{
                    padding: '7px 14px', borderRadius: '20px', border: '2px solid', cursor: 'pointer',
                    borderColor: aptoCredito ? '#007bff' : '#d1d5db',
                    background: aptoCredito ? '#007bff' : 'white',
                    color: aptoCredito ? 'white' : '#374151',
                    fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                >
                  🏦 Apto crédito hipotecario
                </button>
                <button
                  onClick={() => setAptoCreditoSinPlano(v => !v)}
                  title="Apto crédito sin plano actualizado (ej. Banco Francés)"
                  style={{
                    padding: '7px 14px', borderRadius: '20px', border: '2px solid', cursor: 'pointer',
                    borderColor: aptoCreditoSinPlano ? '#007bff' : '#d1d5db',
                    background: aptoCreditoSinPlano ? '#007bff' : 'white',
                    color: aptoCreditoSinPlano ? 'white' : '#374151',
                    fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                >
                  🏦 Apto crédito sin plano
                </button>
                <span className="fp-grupo">Ubicación</span>
                <input
                  type="text"
                  placeholder="📍 Calle / dirección"
                  value={calle}
                  onChange={e => setCalle(e.target.value)}
                  style={{ ...selectFiltro, minWidth: '160px' }}
                />
                <select value={provincia} onChange={e => { setProvincia(e.target.value); setCiudad(''); setBarrios([]); }} style={{ ...selectFiltro, minWidth: '150px' }}>
                  <option value="">Provincia</option>
                  {provinciasDisponibles.map(pr => <option key={pr} value={pr}>{pr}</option>)}
                </select>
                <select value={ciudad} onChange={e => { setCiudad(e.target.value); setBarrios([]); }} style={{ ...selectFiltro, minWidth: '160px' }}>
                  <option value="">Todas las ciudades</option>
                  {ciudadesDisponibles.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {/* Selector múltiple de barrios */}
                <div ref={refDropdownBarrio} className="fp-barrio-wrap" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setDropdownBarrioAbierto(v => !v)}
                    style={{ ...selectFiltro, minWidth: '150px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {barrios.length === 0 ? 'Barrio...' : barrios.length === 1 ? barrios[0] : `${barrios.length} barrios`}
                    </span>
                    <span style={{ fontSize: '10px', flexShrink: 0 }}>{dropdownBarrioAbierto ? '▲' : '▼'}</span>
                  </button>
                  {dropdownBarrioAbierto && (
                    <div className="fp-barrio-panel" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: 'white', border: '1px solid #dee2e6', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 500, minWidth: '220px', padding: '6px 0' }}>
                      {/* Input para agregar barrio manual */}
                      <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '6px' }}>
                        <input
                          id="barrio-custom-input"
                          type="text"
                          placeholder="Escribir barrio..."
                          style={{ flex: 1, border: '1px solid #dee2e6', borderRadius: '6px', padding: '5px 8px', fontSize: '12px', outline: 'none' }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const val = e.target.value.trim();
                              if (val && !barrios.includes(val)) setBarrios(prev => [...prev, val]);
                              e.target.value = '';
                              e.preventDefault();
                            }
                          }}
                        />
                        <button type="button" style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}
                          onClick={() => {
                            const input = document.getElementById('barrio-custom-input');
                            const val = input.value.trim();
                            if (val && !barrios.includes(val)) setBarrios(prev => [...prev, val]);
                            input.value = '';
                          }}>+</button>
                      </div>
                      {/* Lista de barrios disponibles */}
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {barriosDisponibles.length === 0 && (
                        <p style={{ padding: '10px 14px', margin: 0, color: '#aaa', fontSize: '13px' }}>Sin barrios en el catálogo</p>
                      )}
                      {barriosDisponibles.map(b => (
                        <label key={b} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', color: '#333', background: barrios.includes(b) ? '#eff6ff' : 'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.background = barrios.includes(b) ? '#dbeafe' : '#f8f9fa'}
                          onMouseLeave={e => e.currentTarget.style.background = barrios.includes(b) ? '#eff6ff' : 'transparent'}
                        >
                          <input type="checkbox" checked={barrios.includes(b)}
                            onChange={() => setBarrios(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b])}
                            style={{ accentColor: '#3b82f6', width: '15px', height: '15px', flexShrink: 0 }} />
                          {b}
                        </label>
                      ))}
                      </div>{/* fin scroll */}
                      {barrios.length > 0 && (
                        <div style={{ borderTop: '1px solid #f0f0f0', padding: '6px 14px' }}>
                          <button type="button" onClick={() => setBarrios([])} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '12px', cursor: 'pointer', padding: 0 }}>
                            Limpiar selección
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {filtrosSecundariosActivos > 0 && (
                  <button
                    onClick={() => { setDormitorios(''); setM2Min(''); setM2Max(''); setConCochera(false); setAptoCredito(false); setAptoCreditoSinPlano(false); setCalle(''); setProvincia(''); setCiudad(''); setBarrios([]); }}
                    style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                  >
                    Limpiar estos filtros
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Footer fijo (solo mobile): limpiar + ver resultados */}
          <div className="fp-filtros-footer">
            <button onClick={() => limpiarFiltros()}
              style={{ flexShrink: 0, padding: '12px 16px', background: 'white', border: '1.5px solid #d1d5db', borderRadius: '8px', color: '#374151', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
              Limpiar
            </button>
            <button onClick={toggleFiltrosMobile}
              style={{ flex: 1, padding: '12px 16px', background: '#007bff', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '800', fontSize: '15px', cursor: 'pointer' }}>
              Ver {propiedadesOrdenadas.length} resultado{propiedadesOrdenadas.length !== 1 ? 's' : ''}
            </button>
          </div>

        </div>{/* fin filtros-expandible */}
      </div>

      {/* ── CONTENIDO ── */}
      <div ref={catalogoRef} className="contenido-padding" style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* ── RESUMEN DE FILTROS APLICADOS ── */}
        {hayFiltros && (() => {
          const items = [
            operacion ? { label: 'Operación', valor: operacion } : null,
            tipo !== 'Todos' ? { label: 'Tipo', valor: tipo } : null,
            provincia ? { label: 'Provincia', valor: provincia } : null,
            ciudad ? { label: 'Ciudad', valor: ciudad } : null,
            barrios.length > 0 ? { label: 'Barrio', valor: barrios.join(', ') } : null,
            dormitorios ? { label: 'Dormitorios', valor: `${dormitorios}+` } : null,
            (m2Min || m2Max) ? { label: 'M²', valor: `${m2Min || '0'} – ${m2Max || '∞'}` } : null,
            conCochera ? { label: 'Cochera', valor: 'Sí' } : null,
            aptoCredito ? { label: 'Apto crédito', valor: 'Sí' } : null,
            aptoCreditoSinPlano ? { label: 'Apto crédito sin plano', valor: 'Sí' } : null,
            calle.trim() ? { label: 'Calle', valor: calle.trim() } : null,
            precioMin || precioMax ? { label: 'Precio', valor: `${monedaBusqueda === 'USD' ? 'U$S' : '$'} ${precioMin || '0'} – ${precioMax || '∞'}` } : null,
          ].filter(Boolean);
          return (
            <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'8px', marginBottom:'18px', padding:'10px 14px', background:'#f8fafc', borderRadius:'10px', border:'1px solid #e2e8f0', fontSize:'13px' }}>
              <span style={{ color:'#64748b', fontWeight:'600', marginRight:'2px' }}>Mostrando:</span>
              {items.map(({ label, valor }) => (
                <span key={label} style={{ background:'white', border:'1px solid #cbd5e1', borderRadius:'20px', padding:'3px 10px', color:'#1e3a6e', fontWeight:'600' }}>
                  <span style={{ color:'#94a3b8', fontWeight:'400' }}>{label}: </span>{valor}
                </span>
              ))}
              <button onClick={limpiarFiltros} style={{ marginLeft:'auto', background:'none', border:'none', color:'#e53935', fontSize:'12px', fontWeight:'600', cursor:'pointer', padding:'2px 6px' }}>✕ Limpiar</button>
            </div>
          );
        })()}

        {cargando ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <p style={{ fontSize: '16px', margin: 0 }}>Cargando propiedades...</p>
          </div>

        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#dc2626' }}>
            <p style={{ fontSize: '16px' }}>{error}</p>
          </div>

        ) : vista === "lista" || vista === "grilla" ? (
          propiedadesOrdenadas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏠</div>
              <p style={{ fontSize: '16px', margin: '0 0 20px 0' }}>
                {hayFiltros ? 'No hay propiedades con esos filtros.' : 'Todavía no hay propiedades publicadas.'}
              </p>
              {hayFiltros && (
                <button onClick={limpiarFiltros} style={{ padding: '10px 24px', background: '#007bff', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                  Limpiar filtros
                </button>
              )}
              {hayFiltros && (
                <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                  <NotificarmeCTA onClick={abrirGuardar} />
                </div>
              )}
            </div>
          ) : vista === 'lista' ? (
            <>
              <div className="grid-props" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                {propiedadesPagina.map(p => (
                  <div key={p.id} style={{ position: 'relative' }}>
                    <Link to={`/propiedad/${p.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="prop-card" style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,0.07)' }}>
                      <div style={{ position: 'relative', overflow: 'hidden' }}>
                        <img src={p.fotoThumb || p.fotosThumbs?.[0] || p.fotoUrl || p.fotosUrls?.[0] || '/sin-imagen.svg'} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/sin-imagen.svg'; }} alt={p.titulo} loading="lazy" decoding="async" style={{ width: '100%', height: '210px', objectFit: 'cover', display: 'block' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 50%)' }} />
                        <p style={{ position: 'absolute', bottom: '12px', left: '14px', margin: 0, color: 'white', fontWeight: '900', fontSize: '21px', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                          {precioTexto(p)}
                        </p>
                        <span style={{ position: 'absolute', top: '12px', left: '12px', background: p.operacion === 'Alquiler' ? '#0f3460' : '#1a1a2e', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{p.operacion}</span>
                        {p.destacada && <span style={{ position: 'absolute', top: '12px', right: '12px', background: '#f59e0b', color: '#1a1a2e', padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⭐ Destacada</span>}
                        {p.reservado && (
                          <span style={{ position: 'absolute', top: '12px', right: '12px', background: '#d97706', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.8px' }}>🔖 Reservado</span>
                        )}
                      </div>
                      <div style={{ padding: '16px 18px 18px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ background: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{p.tipoPropiedad}</span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={e => { e.preventDefault(); toggle(p.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '17px', padding: '2px 4px', lineHeight: 1, color: esFavorito(p.id) ? '#e11d48' : '#d1d5db' }} title={esFavorito(p.id) ? 'Quitar de favoritos' : 'Guardar en favoritos'}>
                              {esFavorito(p.id) ? '♥' : '♡'}
                            </button>
                            <button onClick={async e => { e.preventDefault(); const url = `${window.location.origin}/propiedad/${p.id}`; if (navigator.share) { try { await navigator.share({ title: p.titulo, text: `${p.titulo} — ${precioTexto(p)}`, url }); } catch {} } else { await copiarAlPortapapeles(url); alert('¡Link copiado!'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px 4px', borderRadius: '6px', color: '#9ca3af' }} title="Compartir">🔗</button>
                          </div>
                        </div>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: '700', color: '#111827', lineHeight: '1.35', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.titulo}</h3>
                        {(p.direccion || p.barrio || p.ciudad) && (
                          <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#6b7280' }}>📍 {[p.direccion, p.barrio, p.ciudad].filter(Boolean).join(', ')}</p>
                        )}
                        <div style={{ display: 'flex', gap: '14px', color: '#6b7280', fontSize: '13px', flexWrap: 'wrap' }}>
                          {p.habitaciones && <span>🛏️ {p.habitaciones} dorm.</span>}
                          {p.banos && <span>🚿 {p.banos} baños</span>}
                          {p.m2Totales && <span>📐 {p.m2Totales} m²</span>}
                          {p.cocheras && <span>🚗 {p.cocheras}</span>}
                        </div>
                      </div>
                    </div>
                  </Link>
                  </div>
                ))}
              </div>
              <Paginacion pagina={pagina} total={totalPaginas} porPagina={porPagina} onPorPagina={cambiarPorPagina} onChange={p => { setPagina(p); catalogoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />
            </>
          ) : (
            /* GRILLA COMPACTA */
            <>
              <div className="grid-grilla" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '14px' }}>
                {propiedadesPagina.map(p => (
                  <div key={p.id} style={{ position: 'relative' }}>
                  <Link to={`/propiedad/${p.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="prop-card" style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                      <div style={{ position: 'relative', overflow: 'hidden' }}>
                        <img src={p.fotoThumb || p.fotosThumbs?.[0] || p.fotoUrl || p.fotosUrls?.[0] || '/sin-imagen.svg'} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/sin-imagen.svg'; }} alt={p.titulo} loading="lazy" decoding="async" style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.68) 0%, transparent 55%)' }} />
                        <p style={{ position: 'absolute', bottom: '8px', left: '10px', margin: 0, color: 'white', fontWeight: '900', fontSize: '16px', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                          {precioTexto(p)}
                        </p>
                        <span style={{ position: 'absolute', top: '8px', left: '8px', background: p.operacion === 'Alquiler' ? '#0f3460' : '#1a1a2e', color: 'white', padding: '2px 7px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{p.operacion}</span>
                        {p.destacada && <span style={{ position: 'absolute', top: '8px', right: '8px', background: '#f59e0b', color: '#1a1a2e', padding: '2px 7px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⭐</span>}
                        {p.reservado && (
                          <span style={{ position: 'absolute', top: '8px', right: '8px', background: '#d97706', color: 'white', padding: '2px 7px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔖 Reservado</span>
                        )}
                      </div>
                      <div style={{ padding: '10px 12px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ background: '#eff6ff', color: '#3b82f6', padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{p.tipoPropiedad}</span>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button onClick={e => { e.preventDefault(); toggle(p.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '1px 3px', lineHeight: 1, color: esFavorito(p.id) ? '#e11d48' : '#d1d5db' }} title={esFavorito(p.id) ? 'Quitar de favoritos' : 'Guardar en favoritos'}>
                              {esFavorito(p.id) ? '♥' : '♡'}
                            </button>
                            <button onClick={async e => { e.preventDefault(); const url = `${window.location.origin}/propiedad/${p.id}`; if (navigator.share) { try { await navigator.share({ title: p.titulo, text: `${p.titulo} — ${precioTexto(p)}`, url }); } catch {} } else { await copiarAlPortapapeles(url); alert('¡Link copiado!'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '2px 4px', color: '#9ca3af' }} title="Compartir">🔗</button>
                          </div>
                        </div>
                        <h3 style={{ margin: '6px 0 5px', fontSize: '13px', fontWeight: '700', color: '#111827', lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.titulo}</h3>
                        {(p.barrio || p.ciudad) && (
                          <p style={{ margin: '0 0 5px', fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            📍 {[p.barrio, p.ciudad].filter(Boolean).join(', ')}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '8px', color: '#9ca3af', fontSize: '11px', flexWrap: 'wrap' }}>
                          {p.habitaciones && <span>🛏️ {p.habitaciones}</span>}
                          {p.m2Totales && <span>📐 {p.m2Totales}m²</span>}
                          {p.banos && <span>🚿 {p.banos}</span>}
                        </div>
                      </div>
                    </div>
                  </Link>
                  </div>
                ))}
              </div>
              <Paginacion pagina={pagina} total={totalPaginas} porPagina={porPagina} onPorPagina={cambiarPorPagina} onChange={p => { setPagina(p); catalogoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />
            </>
          )

        ) : (
          /* MAPA */
          <div style={{ height: '620px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <MapContainer center={formosaCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {propiedadesOrdenadas.map(p => {
                const lat = (p.lat && Math.abs(p.lat) > 1) ? p.lat : -26.1848;
                const lng = (p.lng && Math.abs(p.lng) > 1) ? p.lng : -58.1731;
                return (
                  <Marker key={p.id} position={[lat, lng]}>
                    <Popup>
                      <div style={{ textAlign: 'center', width: '185px' }}>
                        <img src={p.fotoThumb || p.fotosThumbs?.[0] || p.fotoUrl || p.fotosUrls?.[0] || '/sin-imagen.svg'} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/sin-imagen.svg'; }} alt={p.titulo} loading="lazy" decoding="async" style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }} />
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', lineHeight: '1.3' }}>{p.titulo}</h4>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#007bff', fontSize: '15px' }}>
                          {precioTexto(p)}
                        </p>
                        <Link to={`/propiedad/${p.id}`} style={{ display: 'block', background: '#1a1a2e', color: 'white', padding: '7px', borderRadius: '6px', textDecoration: 'none', fontSize: '12px', fontWeight: 'bold' }}>
                          Ver detalles →
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        )}

        {hayFiltros && propiedadesOrdenadas.length > 0 && <NotificarmeCTA onClick={abrirGuardar} />}
      </div>
    </>
  );
}

function NotificarmeCTA({ onClick }) {
  return (
    <div style={{ marginTop: '36px', background: '#0f3460', borderRadius: '16px', padding: '30px 24px', textAlign: 'center', color: 'white' }}>
      <p style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '800' }}>¿No encontraste lo que buscás?</p>
      <p style={{ margin: '0 auto 20px', fontSize: '14px', color: '#cbd5e1', maxWidth: '470px', lineHeight: 1.5 }}>
        Anotate y te avisamos por email apenas ingrese una propiedad que coincida con tu búsqueda.
      </p>
      <button onClick={onClick} style={{ padding: '13px 30px', background: 'white', color: '#0f3460', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '15px' }}>
        🔔 Avisame cuando ingrese
      </button>
    </div>
  );
}

function Paginacion({ pagina, total, onChange, porPagina, onPorPagina }) {
  // Genera el rango de páginas a mostrar: máx 5 botones centrados en la actual
  const rango = [];
  const delta = 2;
  const desde = Math.max(1, pagina - delta);
  const hasta = Math.min(total, pagina + delta);
  for (let i = desde; i <= hasta; i++) rango.push(i);

  const btnBase = { minWidth: '36px', height: '36px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', background: 'white', color: '#374151', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' };
  const btnActivo = { ...btnBase, background: '#1a1a2e', color: 'white', borderColor: '#1a1a2e' };
  const btnDis = { ...btnBase, cursor: 'default', opacity: 0.4 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '36px' }}>
      {total > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <button style={pagina === 1 ? btnDis : btnBase} disabled={pagina === 1} onClick={() => onChange(pagina - 1)}>← Anterior</button>
          {desde > 1 && <>
            <button style={btnBase} onClick={() => onChange(1)}>1</button>
            {desde > 2 && <span style={{ color: '#9ca3af', fontSize: '13px' }}>…</span>}
          </>}
          {rango.map(n => (
            <button key={n} style={n === pagina ? btnActivo : btnBase} onClick={() => onChange(n)}>{n}</button>
          ))}
          {hasta < total && <>
            {hasta < total - 1 && <span style={{ color: '#9ca3af', fontSize: '13px' }}>…</span>}
            <button style={btnBase} onClick={() => onChange(total)}>{total}</button>
          </>}
          <button style={pagina === total ? btnDis : btnBase} disabled={pagina === total} onClick={() => onChange(pagina + 1)}>Siguiente →</button>
        </div>
      )}
      {onPorPagina && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '13px' }}>
          <span>Mostrar</span>
          <select value={porPagina} onChange={e => onPorPagina(Number(e.target.value))} style={selectFiltro}>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
          </select>
          <span>por página</span>
        </div>
      )}
    </div>
  );
}

// Estilos base
const selectFiltro = {
  padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db',
  fontSize: '13px', color: '#374151', background: 'white', cursor: 'pointer',
  outline: 'none', boxSizing: 'border-box',
};
